import React, { useRef, useState } from "react";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";

import { useUploadDoctorMedia } from "../../apis/doctor";
import { setDoctorInfo } from "../../store/authSlice";
import customToast, { ToastType } from "../Toast";

interface Props {
  doctorId: string;
  currentImage?: string;
}

const EditableProfilePicture: React.FC<Props> = ({
  doctorId,
  currentImage,
}) => {
  const dispatch = useAppDispatch();
  const { doctorInfo } = useAppSelector((state) => state.auth);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const { mutateAsync: uploadMedia } = useUploadDoctorMedia();

  const handleImageUpload = async (file: File) => {
    setUploading(true);

    const tempUrl = URL.createObjectURL(file);
    setPreviewImage(tempUrl);

    try {
      // Step 1: Upload to server (e.g., S3)
      const formData = new FormData();
      formData.append("file", file);

      const imageUrl = await uploadMedia({
        doctorId,
        tag: "profile-picture",
        formData,
      });

      // Step 3: Update Redux
      dispatch(setDoctorInfo({ ...doctorInfo, profilePicture: imageUrl }));

      customToast("Profile picture updated", ToastType.success);
    } catch (err) {
      console.error("Upload failed:", err);
      customToast("Failed to update picture", ToastType.error);
    } finally {
      setUploading(false);
    }
  };

  // 🔁 Final image source: local preview → uploaded → fallback
  const imageSrc = previewImage?.startsWith("blob:")
    ? previewImage // don't add ?t to blob URLs
    : `${
        doctorInfo?.profilePicture || currentImage || "/default-avatar.png"
      }?t=${Date.now()}`;

  return (
    <div className="flex flex-col items-center gap-2">
      <img
        src={imageSrc}
        alt="Doctor Profile"
        className="w-24 h-24 rounded-full object-cover cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
        title="Click to change"
      />
      <input
        type="file"
        accept="image/*"
        hidden
        ref={fileInputRef}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImageUpload(file);
        }}
      />
      {uploading && (
        <span className="text-sm text-gray-500 animate-pulse">
          Uploading...
        </span>
      )}
    </div>
  );
};

export default EditableProfilePicture;